/**
 * Utilitaire centralisé pour la construction d'URLs API
 * 
 * Best Practice: Centraliser toute la logique de construction d'URLs
 * pour éviter les erreurs de concaténation et assurer la cohérence
 */

/**
 * Détermine l'URL de base de l'API selon l'environnement
 * @returns L'URL de base (sans slash final)
 */
export function getApiBaseUrl(): string {
  // 1. Vérifier d'abord la variable d'environnement (priorité la plus haute)
  if (process.env.REACT_APP_API_URL) {
    const envUrl = process.env.REACT_APP_API_URL.trim();
    // Normaliser : retirer le slash final
    return envUrl.endsWith('/') ? envUrl.slice(0, -1) : envUrl;
  }

  // 2. Détection automatique au runtime
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    
    // Environnement de production (serveur distant)
    if (
      hostname !== 'localhost' &&
      hostname !== '127.0.0.1' &&
      !hostname.startsWith('192.168.') &&
      !hostname.startsWith('10.') &&
      !hostname.startsWith('172.16.')
    ) {
      return '/api/v1';
    }
  }

  // 3. Fallback : développement local
  return 'http://localhost:8000/api/v1';
}

/**
 * Construit une URL complète à partir d'un endpoint
 * Gère automatiquement les slashes et la normalisation
 * 
 * @param endpoint - L'endpoint (ex: '/users/login/' ou 'users/login/')
 * @returns L'URL complète
 * 
 * @example
 * buildApiUrl('/users/login/') // => '/api/v1/users/login/' (production)
 * buildApiUrl('users/login/')   // => '/api/v1/users/login/' (production)
 * buildApiUrl('/users/login/') // => 'http://localhost:8000/api/v1/users/login/' (dev)
 */
export function buildApiUrl(endpoint: string): string {
  const baseUrl = getApiBaseUrl();
  
  // Normaliser l'endpoint : s'assurer qu'il commence par un slash
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  
  // Construire l'URL
  const fullUrl = `${baseUrl}${normalizedEndpoint}`;
  
  // Validation finale : détecter et corriger les erreurs courantes
  if (fullUrl.includes('/apiv1') || fullUrl.includes('apiv1')) {
    console.warn('⚠️ URL malformée détectée, correction automatique:', fullUrl);
    return fullUrl.replace(/\/apiv1/g, '/api/v1').replace(/apiv1/g, 'api/v1');
  }
  
  return fullUrl;
}

/**
 * Construit une URL avec des paramètres de requête
 * 
 * @param endpoint - L'endpoint
 * @param params - Paramètres de requête (optionnel)
 * @returns L'URL complète avec paramètres
 * 
 * @example
 * buildApiUrlWithParams('/users/', { page: 1, limit: 10 })
 * // => '/api/v1/users/?page=1&limit=10'
 */
export function buildApiUrlWithParams(
  endpoint: string,
  params?: Record<string, any>
): string {
  const baseUrl = buildApiUrl(endpoint);
  
  if (!params || Object.keys(params).length === 0) {
    return baseUrl;
  }
  
  // Construire les paramètres de requête
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });
  
  const queryString = searchParams.toString();
  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
}

/**
 * Configuration API centralisée
 */
export const API_CONFIG = {
  get BASE_URL() {
    return getApiBaseUrl();
  },
  TIMEOUT: 10000,
};
